<template>
  <BaseSelect
    ref="selectComponent"
    v-model="validationValue"
    :name="props.name"
    :placeholder="props.placeholder"
    :options="availableOptions"
    :rounded="props.rounded"
    :rules="[checkRequiredRule, checkSelectedOptionsLimitRule]"
    :disabled="props.disabled"
    @change="addOption"
  >
    <template #above>
      <label
        v-if="props.label"
        class="block text-sm font-medium leading-5 mb-1"
        :class="[rounded === 'full' ? 'px-6' : 'px-4']"
      >
        {{ props.label }}
        {{ props.required ? '*' : '' }}
      </label>
      <div v-if="validSelectedValues.length > 0" class="mb-2 mt-1">
        <div class="flex flex-wrap -m-1 max-w-full">
          <div
            v-for="(option, idx) in validSelectedValues"
            :key="idx"
            class="max-w-full"
          >
            <slot
              v-if="$slots.option"
              name="option"
              v-bind="{
                value: option,
                label: options.find(({ value }) => option === value)?.label,
                removeOption,
                disabled: props.disabled,
              }"
            />
            <BaseButton
              v-else
              class="h-8 pr-2 pl-3 m-1 max-w-full"
              size="none"
              look="border"
              :disabled="props.disabled"
              @click="() => removeOption(option)"
            >
              <span class="truncate max-w-full">
                {{ options.find(({ value }) => option === value)?.label }}
              </span>
              <BaseIcon name="outline_x" class="ml-2" size="xs" />
            </BaseButton>
          </div>
        </div>
      </div>
    </template>
  </BaseSelect>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { PropType } from 'vue'
import type { OptionInterface } from './use-simple-input'
import type BaseSelect from '~/components/base/controls/BaseSelect.vue'

const { t } = useI18n()

const roundedMap = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  '3xl': 'rounded-3xl',
  full: 'rounded-full',
}

const props = defineProps({
  modelValue: {
    type: Array as PropType<Array<string | number>>,
    default: () => [],
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  rounded: {
    type: String as PropType<keyof typeof roundedMap>,
    default: 'xl',
  },
  name: {
    type: String,
    default: '',
  },
  options: {
    type: Array as PropType<Readonly<OptionInterface[]>>,
    default: () => [],
  },
  selectedOptionsLimit: {
    type: Number,
    default: undefined,
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})
const emits = defineEmits(['update:modelValue'])

const selectComponent = ref<InstanceType<typeof BaseSelect> | null>(null)

// to show validation that value is not empty
// else we have an error that the value is empty when props.required is "true"
const validationValue = ref('')

const availableOptions = computed(() => {
  return props.options.filter(
    (item) => !props.modelValue.find((value) => value === item.value)
  )
})
const validSelectedValues = computed(() => {
  return (
    props.modelValue?.filter((value) => {
      const valueFound = props.options.find((option) => option.value === value)

      if (!valueFound) {
        console.warn('Non-existent value selected in multiselect component')
      }
      return valueFound
    }) || []
  )
})

function updateValidationValue() {
  nextTick(() => {
    validationValue.value = props.modelValue.join(',')
    selectComponent.value?.validate()
  })
}

function addOption({ value }: OptionInterface) {
  if (props.disabled) {
    return
  }

  emits('update:modelValue', [...props.modelValue, value])
  updateValidationValue()
}

function removeOption(option: string | number) {
  if (props.disabled) {
    return
  }

  emits(
    'update:modelValue',
    props.modelValue.filter((value) => option !== value)
  )
  updateValidationValue()
}

function checkRequiredRule(): string | boolean {
  return props.required && !props.modelValue?.length
    ? t('validation.required')
    : true
}

function checkSelectedOptionsLimitRule(): string | boolean {
  if (
    props.name &&
    props.selectedOptionsLimit &&
    props.modelValue.length > props.selectedOptionsLimit
  ) {
    return (
      t('validation.selected_options_limit', {
        limit: props.selectedOptionsLimit,
      }) || false
    )
  }

  return true
}
</script>
